import React, { useEffect, useRef, useState } from "react";
import { useLoading } from "./LoadingContext";

/** Duration (ms) to wait for the CSS fade-out transition before unmounting. */
const FADE_OUT_DURATION = 400;

/**
 * GlobalLoader
 *
 * Renders a fullscreen overlay with the animated Senco GIF logo.
 * The overlay fades in/out smoothly and blocks all user interaction while visible.
 *
 * This component should be rendered once at the top of the component tree,
 * typically inside `_app.tsx` within the `<LoadingProvider>`.
 */
const GlobalLoader: React.FC = () => {
  const { isLoading } = useLoading();

  /** Controls whether the overlay is in the DOM. */
  const [shouldRender, setShouldRender] = useState(false);
  /** Controls the CSS visibility class (fade-in trigger). */
  const [isVisible, setIsVisible] = useState(false);

  const fadeOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      // Cancel any pending fade-out removal
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }

      // Mount overlay in DOM
      setShouldRender(true);

      // Double-rAF: ensures the element is painted at opacity 0 before
      // we add the visible class to trigger the CSS transition.
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setIsVisible(true);
          rafRef.current = null;
        });
      });
    } else {
      // Cancel any pending rAF (in case hide fires before paint)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Start fade-out
      setIsVisible(false);

      // Remove from DOM after CSS transition completes
      fadeOutTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        fadeOutTimerRef.current = null;
      }, FADE_OUT_DURATION);
    }
  }, [isLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (!shouldRender) return null;

  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "";

  return (
    <div
      className={`global-loader-overlay${
        isVisible ? " global-loader-visible" : ""
      }`}
      role="alert"
      aria-busy={isLoading}
      aria-live="assertive"
    >
      <div className="global-loader-content">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`${basePath}/img/senco-loader.gif`}
          alt=""
          className="global-loader-logo"
          draggable={false}
        />
        <span className="global-loader-sr-only">Loading, please wait…</span>
      </div>
    </div>
  );
};

export default GlobalLoader;
